import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, mrSession } from "@/lib/apiClient";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogIn, Loader2, ArrowLeft, BadgeCheck } from "lucide-react";
import { ORG } from "@/lib/constants";

export default function MrLoginPage() {
  const navigate = useNavigate();
  const [empCode, setEmpCode] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const login = async (e) => {
    e?.preventDefault();
    if (!empCode.trim() || !password) { toast.error("Enter your Employee ID and password."); return; }
    setLoading(true);
    try {
      const res = await api.post("/mr/login", { emp_code: empCode.trim(), password });
      mrSession.save(res.data.token, res.data.mr);
      toast.success(`Welcome, ${res.data.mr.name}`);
      navigate("/pledge");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App bg-slate-50 min-h-screen">
      <Navbar />
      <div className="max-w-md mx-auto px-5 py-16">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0056D2] mb-6" data-testid="mr-login-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <form onSubmit={login} className="rounded-3xl bg-white border border-slate-200 p-8">
          <div className="flex items-center gap-3 mb-2">
            <img src={ORG.logo} alt={`${ORG.name} logo`} className="w-10 h-10" />
            <div>
              <h1 className="font-display font-extrabold text-2xl text-slate-900 leading-none">MR Login</h1>
              <p className="text-xs text-slate-500 mt-1">{ORG.name} field team</p>
            </div>
          </div>
          <p className="mt-3 text-sm text-slate-500 flex items-start gap-2">
            <BadgeCheck className="w-4 h-4 text-[#138808] mt-0.5 shrink-0" />
            Log in with your Employee ID to enroll doctors on the pledge wall. First-time password is your Employee ID.
          </p>
          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="emp" className="text-sm font-medium text-slate-700">Employee ID</Label>
              <Input data-testid="mr-empcode-input" id="emp" value={empCode} onChange={(e) => setEmpCode(e.target.value)} className="mt-1.5" placeholder="e.g. 19162" autoComplete="username" />
            </div>
            <div>
              <Label htmlFor="pwd" className="text-sm font-medium text-slate-700">Password</Label>
              <Input data-testid="mr-password-input" id="pwd" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" placeholder="Password" autoComplete="current-password" />
            </div>
            <Button data-testid="mr-login-btn" type="submit" disabled={loading} size="lg" className="w-full rounded-full bg-[#0056D2] hover:bg-blue-700 text-white font-semibold h-12">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in…</> : <><LogIn className="w-4 h-4 mr-2" /> Login</>}
            </Button>
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
}
